import { useMutation, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';
import { useRefreshAuth, type BusinessUnitDto } from '@/hooks/api/useAuth';

const httpClient = new HttpClient();

export type LogoUploadUrlResponse = {
  uploadUrl: string;
  s3Key: string;
};

export async function requestBusinessUnitLogoUploadUrl(
  businessUnitId: number,
  fileExtension: string,
  contentType: string,
): Promise<LogoUploadUrlResponse> {
  const res = await httpClient.get<LogoUploadUrlResponse>(
    `api/business-units/${businessUnitId}/logo-upload-url`,
    { fileExtension, contentType } as Record<string, string>,
  );
  return res.data as unknown as LogoUploadUrlResponse;
}

export async function confirmBusinessUnitLogo(
  businessUnitId: number,
  s3Key: string,
): Promise<{ businessUnit: BusinessUnitDto }> {
  const res = await httpClient.post(`api/business-units/${businessUnitId}/logo`, {
    s3Key,
  });
  return res.data as { businessUnit: BusinessUnitDto };
}

export async function deleteBusinessUnitLogo(
  businessUnitId: number,
): Promise<{ businessUnit: BusinessUnitDto }> {
  const res = await httpClient.delete(`api/business-units/${businessUnitId}/logo`);
  return res.data as { businessUnit: BusinessUnitDto };
}

export async function requestBusinessUnitIconUploadUrl(
  businessUnitId: number,
  fileExtension: string,
  contentType: string,
): Promise<LogoUploadUrlResponse> {
  const res = await httpClient.get<LogoUploadUrlResponse>(
    `api/business-units/${businessUnitId}/icon-upload-url`,
    { fileExtension, contentType } as Record<string, string>,
  );
  return res.data as unknown as LogoUploadUrlResponse;
}

export async function confirmBusinessUnitIcon(
  businessUnitId: number,
  s3Key: string,
): Promise<{ businessUnit: BusinessUnitDto }> {
  const res = await httpClient.post(`api/business-units/${businessUnitId}/icon`, {
    s3Key,
  });
  return res.data as { businessUnit: BusinessUnitDto };
}

export async function deleteBusinessUnitIcon(
  businessUnitId: number,
): Promise<{ businessUnit: BusinessUnitDto }> {
  const res = await httpClient.delete(`api/business-units/${businessUnitId}/icon`);
  return res.data as { businessUnit: BusinessUnitDto };
}

export async function patchBusinessUnitName(
  businessUnitId: number,
  name: string,
): Promise<BusinessUnitDto> {
  const res = await httpClient.patch<BusinessUnitDto>(`api/business-units/${businessUnitId}`, {
    name,
  });
  return res.data as BusinessUnitDto;
}

/**
 * Presigned PUT to S3, then confirm on API; on success invalidates BUs and refreshes session (logo in header).
 */
export function useUploadBusinessUnitLogo() {
  const queryClient = useQueryClient();
  const { refetch: refetchAuth } = useRefreshAuth();

  return useMutation({
    mutationFn: async ({
      businessUnitId,
      file,
    }: {
      businessUnitId: number;
      file: File;
    }) => {
      const fileExtension = (file.name.split('.').pop() || 'png').toLowerCase();
      const contentType = file.type || 'image/png';
      const { uploadUrl, s3Key } = await requestBusinessUnitLogoUploadUrl(
        businessUnitId,
        fileExtension,
        contentType,
      );
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      if (!put.ok) {
        throw new Error('Upload to storage failed');
      }
      return confirmBusinessUnitLogo(businessUnitId, s3Key);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', 'all'] });
      await refetchAuth();
    },
  });
}

export function useDeleteBusinessUnitLogo() {
  const queryClient = useQueryClient();
  const { refetch: refetchAuth } = useRefreshAuth();

  return useMutation({
    mutationFn: (businessUnitId: number) =>
      deleteBusinessUnitLogo(businessUnitId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', 'all'] });
      await refetchAuth();
    },
  });
}

export function useUploadBusinessUnitIcon() {
  const queryClient = useQueryClient();
  const { refetch: refetchAuth } = useRefreshAuth();

  return useMutation({
    mutationFn: async ({
      businessUnitId,
      file,
    }: {
      businessUnitId: number;
      file: File;
    }) => {
      const fileExtension = (file.name.split('.').pop() || 'png').toLowerCase();
      const contentType = file.type || 'image/png';
      const { uploadUrl, s3Key } = await requestBusinessUnitIconUploadUrl(
        businessUnitId,
        fileExtension,
        contentType,
      );
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      if (!put.ok) {
        throw new Error('Upload to storage failed');
      }
      return confirmBusinessUnitIcon(businessUnitId, s3Key);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', 'all'] });
      await refetchAuth();
    },
  });
}

export function useDeleteBusinessUnitIcon() {
  const queryClient = useQueryClient();
  const { refetch: refetchAuth } = useRefreshAuth();

  return useMutation({
    mutationFn: (businessUnitId: number) => deleteBusinessUnitIcon(businessUnitId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', 'all'] });
      await refetchAuth();
    },
  });
}

export function useUpdateBusinessUnitName() {
  const queryClient = useQueryClient();
  const { refetch: refetchAuth } = useRefreshAuth();

  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      patchBusinessUnitName(id, name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', 'all'] });
      await refetchAuth();
    },
  });
}

export async function patchBusinessUnitThemeFont(
  businessUnitId: number,
  themeFontKey: string,
): Promise<BusinessUnitDto> {
  // POST (not PATCH): some production proxies/WAFs block PATCH; server accepts both.
  const res = await httpClient.post(`api/business-units/${businessUnitId}/theme-font`, {
    themeFontKey,
  });
  const body = res.data as { data?: BusinessUnitDto };
  if (body && typeof body === 'object' && 'data' in body && body.data) {
    return body.data;
  }
  return body as unknown as BusinessUnitDto;
}

function mergeBusinessUnitThemeIntoCachedUser(
  patched: BusinessUnitDto,
  businessUnitId: number,
  prev: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!prev) return null;
  const next: Record<string, unknown> = { ...prev };
  const cur = prev.currentBusinessUnit as BusinessUnitDto | undefined;
  if (cur?.id === businessUnitId) {
    next.currentBusinessUnit = { ...cur, ...patched };
  }
  const list = prev.businessUnits as BusinessUnitDto[] | undefined;
  if (list?.length) {
    next.businessUnits = list.map((bu) =>
      bu.id === businessUnitId ? { ...bu, ...patched } : bu,
    );
  }
  return next;
}

export function usePatchBusinessUnitThemeFont() {
  const queryClient = useQueryClient();
  const { refetch: refetchAuth } = useRefreshAuth();

  return useMutation({
    mutationFn: ({
      businessUnitId,
      themeFontKey,
    }: {
      businessUnitId: number;
      themeFontKey: string;
    }) => patchBusinessUnitThemeFont(businessUnitId, themeFontKey),
    onSuccess: async (patched, { businessUnitId }) => {
      const prev = queryClient.getQueryData<Record<string, unknown>>(['currentUser']);
      const next = mergeBusinessUnitThemeIntoCachedUser(patched, businessUnitId, prev);
      if (next) {
        queryClient.setQueryData(['currentUser'], next);
      }
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', 'all'] });
      await refetchAuth();
    },
  });
}

export async function patchBusinessUnitCountCalendarTimezone(
  businessUnitId: number,
  countCalendarTimeZone: string,
): Promise<BusinessUnitDto> {
  const res = await httpClient.post(`api/business-units/${businessUnitId}/count-calendar-timezone`, {
    countCalendarTimeZone,
  });
  const body = res.data as { data?: BusinessUnitDto };
  if (body && typeof body === 'object' && 'data' in body && body.data) {
    return body.data;
  }
  return body as unknown as BusinessUnitDto;
}

export function usePatchBusinessUnitCountCalendarTimezone() {
  const queryClient = useQueryClient();
  const { refetch: refetchAuth } = useRefreshAuth();

  return useMutation({
    mutationFn: ({
      businessUnitId,
      countCalendarTimeZone,
    }: {
      businessUnitId: number;
      countCalendarTimeZone: string;
    }) => patchBusinessUnitCountCalendarTimezone(businessUnitId, countCalendarTimeZone),
    onSuccess: async (patched, { businessUnitId }) => {
      const prev = queryClient.getQueryData<Record<string, unknown>>(['currentUser']);
      const next = mergeBusinessUnitThemeIntoCachedUser(patched, businessUnitId, prev);
      if (next) {
        queryClient.setQueryData(['currentUser'], next);
      }
      await queryClient.invalidateQueries({ queryKey: ['businessUnits', 'all'] });
      await refetchAuth();
    },
  });
}
